/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

// @mui material components
import { useEffect, useState } from 'react'

import Alert from '@mui/material/Alert'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Icon from '@mui/material/Icon'
import { loadStripe } from '@stripe/stripe-js'
import axios from 'axios'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { useAuth } from 'context/auth'
import type { PaymentMethodResponse, StripePaymentMethod } from 'types/server/stripe'

function Billing(): JSX.Element {
	const { user } = useAuth()
	const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethod[]>([])
	const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string | undefined>()
	const [loading, setLoading] = useState(true)
	const [actionLoading, setActionLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
	const [methodToDelete, setMethodToDelete] = useState<StripePaymentMethod | null>(null)
	const [deleteLoading, setDeleteLoading] = useState(false)
	const [setDefaultLoading, setSetDefaultLoading] = useState<string | null>(null)
	const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}`)

	useEffect(() => {
		if (user?.auth?.uid) {
			fetchPaymentMethods()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.auth?.uid])

	const fetchPaymentMethods = async () => {
		try {
			setLoading(true)
			setError(null)
			const response = await axios.get<PaymentMethodResponse>(
				`${process.env.REACT_APP_API_URL}/stripe/payment-methods`,
				{
					headers: {
						Authorization: await user.auth.getIdToken(),
					},
				}
			)
			setPaymentMethods(response.data.paymentMethods)
			setDefaultPaymentMethod(response.data.defaultPaymentMethod)
		} catch (err) {
			console.error('Error fetching payment methods:', err)
			setError('Failed to load payment methods')
		} finally {
			setLoading(false)
		}
	}

	const handleSubscribe = async (uid: string) => {
		try {
			setActionLoading(true)

			const res = await axios.post<{ sessionId: string }>(
				`${process.env.REACT_APP_API_URL}/stripe/create-session`,
				{
					uid: uid,
					mode: 'subscription',
					priceId: process.env.REACT_APP_STRIPE_PRICE_ID,
				},
				{
					headers: {
						Authorization: await user.auth.getIdToken(),
					},
				}
			)
			const { sessionId } = res.data

			const stripe = await stripePromise
			await stripe.redirectToCheckout({ sessionId })
		} catch (err) {
			console.error('Error creating checkout session:', err)
			setError('Failed to start payment setup')
		} finally {
			setActionLoading(false)
		}
	}

	const handleStripeCustomerPortal = async () => {
		try {
			setActionLoading(true)
			const res = await axios.post<{ url: string }>(
				`${process.env.REACT_APP_API_URL}/stripe/create-portal-session`,
				{},
				{
					headers: {
						Authorization: await user.auth.getIdToken(),
					},
				}
			)
			const { url } = res.data
			window.location.href = url
		} catch (err) {
			console.error('Error creating portal session:', err)
			setError('Failed to open customer portal')
		} finally {
			setActionLoading(false)
		}
	}
	useEffect(() => {
		if (user?.auth?.uid) {
			fetchPaymentMethods()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.auth?.uid])

	const formatCardBrand = (brand: string) => {
		const brandMap: Record<string, string> = {
			visa: 'Visa',
			mastercard: 'Mastercard',
			amex: 'American Express',
			discover: 'Discover',
			diners: 'Diners Club',
			jcb: 'JCB',
			unionpay: 'UnionPay',
		}
		return brandMap[brand] || brand.toUpperCase()
	}

	const getCardIcon = (brand: string) => {
		const iconMap: Record<string, string> = {
			visa: 'credit_card',
			mastercard: 'credit_card',
			amex: 'credit_card',
			discover: 'credit_card',
			diners: 'credit_card',
			jcb: 'credit_card',
			unionpay: 'credit_card',
		}
		return iconMap[brand] || 'credit_card'
	}

	const handleSetAsDefault = async (method: StripePaymentMethod) => {
		if (method.id === defaultPaymentMethod) {
			return // Already default
		}

		try {
			setSetDefaultLoading(method.id)
			setError(null)

			// Call API to set payment method as default
			await axios.post(
				`${process.env.REACT_APP_API_URL}/stripe/payment-methods/default`,
				{
					paymentMethodId: method.id,
				},
				{
					headers: {
						Authorization: await user.auth.getIdToken(),
					},
				}
			)

			// Update local state
			setDefaultPaymentMethod(method.id)
		} catch (err) {
			console.error('Error setting default payment method:', err)
			setError('Failed to set default payment method. Please try again.')
		} finally {
			setSetDefaultLoading(null)
		}
	}

	const handleDeleteConfirm = (method: StripePaymentMethod) => {
		setMethodToDelete(method)
		setDeleteConfirmOpen(true)
	}

	const handleDeletePaymentMethod = async () => {
		if (!methodToDelete) return

		try {
			setDeleteLoading(true)
			setError(null)

			await axios.delete(
				`${process.env.REACT_APP_API_URL}/stripe/payment-method/${methodToDelete.id}`,
				{
					headers: {
						Authorization: await user.auth.getIdToken(),
					},
				}
			)

			setPaymentMethods(prev => prev.filter(method => method.id !== methodToDelete.id))

			if (methodToDelete.id === defaultPaymentMethod) {
				setDefaultPaymentMethod(undefined)
			}

			setDeleteConfirmOpen(false)
			setMethodToDelete(null)
		} catch (err) {
			console.error('Error deleting payment method:', err)
			setError(
				'Failed to delete payment method. You may need to change your default payment method first.'
			)
		} finally {
			setDeleteLoading(false)
		}
	}

	const handleDeleteCancel = () => {
		setDeleteConfirmOpen(false)
		setMethodToDelete(null)
	}
	return (
		<Card id="billing">
			<MDBox p={2}>
				{/* Header Section */}
				<Grid container spacing={3} alignItems="center" mb={3}>
					<Grid item>
						<MDBox height="100%" mt={0.5} lineHeight={1}>
							<MDTypography variant="h5" fontWeight="medium">
								Billing & Payment Methods
							</MDTypography>
							<MDTypography variant="body2" color="text" mt={0.5}>
								Manage your subscription and payment methods
							</MDTypography>
						</MDBox>
					</Grid>
				</Grid>

				{/* Error Alert */}
				{error && (
					<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
						{error}
					</Alert>
				)}

				{/* Upcoming Invoice Section */}
				<MDBox mb={3}>
					<Grid container spacing={3} alignItems="center">
						<Grid item>
							<MDBox height="100%" mt={0.5} lineHeight={1}>
								<MDTypography variant="h6" fontWeight="medium">
									Upcoming Invoice
								</MDTypography>
							</MDBox>
						</Grid>
						<Grid item xs={12} md={6} lg={3} sx={{ ml: 'auto' }}>
							<MDBox
								display="flex"
								justifyContent={{ md: 'flex-end' }}
								alignItems="center"
								lineHeight={1}
								gap={2}
							>
								<MDBox mr={2} alignItems="center" display={'flex'} flexDirection={'column'}>
									<MDTypography component="h6" variant="button" fontWeight="medium">
										£150.00
									</MDTypography>
								</MDBox>
								<MDButton
									color="primary"
									variant="outlined"
									onClick={() => handleStripeCustomerPortal()}
									disabled={actionLoading}
									startIcon={actionLoading ? <CircularProgress size={16} /> : undefined}
								>
									Manage Billing
								</MDButton>
							</MDBox>
						</Grid>
					</Grid>
				</MDBox>

				<Divider />

				{/* Payment Methods Section */}
				<MDBox mt={3}>
					<Grid container spacing={3} alignItems="center">
						<Grid item>
							<MDBox height="100%" mt={0.5} lineHeight={1}>
								<MDTypography variant="h6" fontWeight="medium">
									Payment Methods
								</MDTypography>
							</MDBox>
						</Grid>
						<Grid item xs={12} md={6} lg={3} sx={{ ml: 'auto' }}>
							<MDBox
								display="flex"
								justifyContent={{ md: 'flex-end' }}
								alignItems="center"
								lineHeight={1}
								gap={2}
							>
								<MDButton
									color="primary"
									variant="outlined"
									onClick={() => handleSubscribe(user.auth.uid)}
									disabled={actionLoading}
									startIcon={actionLoading ? <CircularProgress size={16} /> : undefined}
								>
									Manage Payment Method
								</MDButton>
							</MDBox>
						</Grid>
					</Grid>
					<MDBox mt={3} mb={2} />

					{loading ? (
						<MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
							<CircularProgress />
							<MDTypography variant="body2" ml={2}>
								Loading payment methods...
							</MDTypography>
						</MDBox>
					) : paymentMethods.length === 0 ? (
						<MDBox
							border="1px dashed"
							borderColor="grey.300"
							borderRadius="8px"
							p={4}
							textAlign="center"
						>
							<Icon fontSize="large" color="action" sx={{ mb: 1 }}>
								credit_card_off
							</Icon>
							<MDTypography variant="h6" color="text" mb={1}>
								No Payment Methods
							</MDTypography>
							<MDTypography variant="body2" color="text" mb={2}>
								Add a payment method to manage your subscription
							</MDTypography>
						</MDBox>
					) : (
						<Grid container spacing={2}>
							{paymentMethods.map(method => (
								<Grid item xs={12} md={6} lg={4} key={method.id}>
									<Card
										variant="outlined"
										sx={{
											p: 2,
											position: 'relative',
											border: method.id === defaultPaymentMethod ? 2 : 1,
											borderColor: method.id === defaultPaymentMethod ? 'primary.main' : 'grey.300',
										}}
									>
										{method.id === defaultPaymentMethod && (
											<Chip
												label="Default"
												color="primary"
												size="small"
												sx={{
													position: 'absolute',
													top: 8,
													right: 8,
													zIndex: 1,
												}}
											/>
										)}

										<MDBox display="flex" alignItems="center" mb={1}>
											<Icon sx={{ mr: 1 }}>
												{method.card ? getCardIcon(method.card.brand) : 'payment'}
											</Icon>
											<MDTypography variant="h6" fontWeight="medium">
												{method.card ? formatCardBrand(method.card.brand) : method.type}
											</MDTypography>
										</MDBox>

										{method.card && (
											<>
												<MDTypography variant="body2" color="text">
													•••• •••• •••• {method.card.last4}
												</MDTypography>
												<MDTypography variant="body2" color="text">
													Expires {method.card.exp_month.toString().padStart(2, '0')}/
													{method.card.exp_year}
												</MDTypography>
											</>
										)}

										<MDBox mt={2} display="flex" gap={1}>
											{/* Only show Set as Default if not already the default payment method */}
											{method.id !== defaultPaymentMethod && (
												<MDButton
													size="small"
													variant="outlined"
													color="primary"
													onClick={() => handleSetAsDefault(method)}
													disabled={setDefaultLoading === method.id || actionLoading}
													startIcon={
														setDefaultLoading === method.id ? (
															<CircularProgress size={16} />
														) : (
															<Icon fontSize="small">star</Icon>
														)
													}
												>
													{setDefaultLoading === method.id ? 'Setting...' : 'Set as Default'}
												</MDButton>
											)}
											{/* Only show delete if not the default payment method or if there are multiple methods */}
											{(method.id !== defaultPaymentMethod || paymentMethods.length > 1) && (
												<MDButton
													size="small"
													variant="outlined"
													color="error"
													onClick={() => handleDeleteConfirm(method)}
													disabled={
														actionLoading || deleteLoading || setDefaultLoading === method.id
													}
													startIcon={<Icon fontSize="small">delete</Icon>}
												>
													Delete
												</MDButton>
											)}
										</MDBox>
									</Card>
								</Grid>
							))}
						</Grid>
					)}
				</MDBox>
				{paymentMethods.length > 0 && (
					<>
						<Divider sx={{ my: 3 }} />
						<MDBox>
							<MDBox display="flex" gap={1} flexWrap="wrap">
								<MDButton
									variant="outlined"
									color="secondary"
									onClick={fetchPaymentMethods}
									disabled={loading}
									startIcon={<Icon>refresh</Icon>}
								>
									Refresh
								</MDButton>
							</MDBox>
						</MDBox>
					</>
				)}
			</MDBox>

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
				<DialogTitle>
					<MDBox display="flex" alignItems="center">
						<Icon color="error" sx={{ mr: 1 }}>
							warning
						</Icon>
						<MDTypography variant="h6">Delete Payment Method</MDTypography>
					</MDBox>
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete this payment method?
					</DialogContentText>
					{methodToDelete && (
						<MDBox mt={2} p={2} border="1px solid" borderColor="grey.300" borderRadius="8px">
							<MDBox display="flex" alignItems="center" mb={1}>
								<Icon sx={{ mr: 1 }}>
									{methodToDelete.card ? getCardIcon(methodToDelete.card.brand) : 'payment'}
								</Icon>
								<MDTypography variant="h6" fontWeight="medium">
									{methodToDelete.card
										? formatCardBrand(methodToDelete.card.brand)
										: methodToDelete.type}
								</MDTypography>
								{methodToDelete.id === defaultPaymentMethod && (
									<Chip label="Default" color="primary" size="small" sx={{ ml: 1 }} />
								)}
							</MDBox>
							{methodToDelete.card && (
								<MDTypography variant="body2" color="text">
									•••• •••• •••• {methodToDelete.card.last4}
								</MDTypography>
							)}
						</MDBox>
					)}
					<DialogContentText sx={{ mt: 2, fontWeight: 'medium' }}>
						{methodToDelete?.id === defaultPaymentMethod
							? 'This is your default payment method. Deleting it may affect your active subscriptions.'
							: 'This action cannot be undone.'}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<MDButton onClick={handleDeleteCancel} color="secondary">
						Cancel
					</MDButton>
					<MDButton
						onClick={handleDeletePaymentMethod}
						color="error"
						variant="contained"
						disabled={deleteLoading}
						startIcon={deleteLoading ? <CircularProgress size={16} /> : <Icon>delete</Icon>}
					>
						{deleteLoading ? 'Deleting...' : 'Delete'}
					</MDButton>
				</DialogActions>
			</Dialog>
		</Card>
	)
}

export default Billing
